import { useCallback } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import Button from "@atlaskit/button";
import { token } from "@atlaskit/tokens";
import { Editions, ProductKeys } from "@atlassiansox/asc-stargate";

import { JSM_PRODUCT_ID } from "@/components/cross-flow";
import { useCrossFlowFrontendTouchpoint } from "@/components/cross-flow/touchpoint";
import { useFireEvent } from "@/components/cross-flow/touchpoint/utils";
import { Picture } from "@/components/wpl/image/src";
import { useLocalizedHref } from "@/libraries/web-framework";

import { JiraServiceManagementTemplateMessageMap } from "../../../../templates/software/jira/templates/service-management/jira-templates-page/src/content/templates-grid-content-intl";

import type { Template } from "../../../../content/templates/template.types";

type TemplateSlug = keyof typeof JiraServiceManagementTemplateMessageMap;

const TEMPLATE_SLUG_TO_ID: Record<TemplateSlug, string> = {
    "it-service-management": "jsm.it-service-management-basic",
    "advanced-it-service-management":
        "com.atlassian.servicedesk:simplified-it-service-management",
    "general-service-management": "jsm.general-service-management",
    "customer-service-management": "jsm.customer-service-management",
    "hr-service-management": "jsm.hr-service-management",
    "finance-service-management": "jsm.finance-service-management",
    "facilities-service-management": "jsm.facilities-service-management",
    "marketing-service-management": "jsm.marketing-service-management",
    "analytics-service-management": "jsm.analytics-service-management",
    "legal-service-management": "jsm.legal-service-management",
    "sales-service-management": "jsm.sales-service-management",
    "design-service-management": "jsm.design-service-management"
} as const;

export const HoverTemplateCard = ({ template }: { template: Template }) => {
    const intl = useIntl();

    const { fireUIEvent, fireCrossFlowInteractedUIEvent } = useFireEvent();

    const templateId = TEMPLATE_SLUG_TO_ID[template.templateId as TemplateSlug];

    const messages =
        JiraServiceManagementTemplateMessageMap[
            template.templateId as TemplateSlug
        ];

    const learnMoreHref = useLocalizedHref(
        `/software/jira/templates/${template.urlSlug}`
    );
    const signupLink = useLocalizedHref(
        "/software/jira/service-management/free"
    );
    const { openCrossFlow } = useCrossFlowFrontendTouchpoint(
        {
            productKey: ProductKeys.JIRA_SERVICE_DESK,
            screenName: "wacJsmTemplateScreen",
            redirectUrl: signupLink
        },
        false
    );

    const handleCardClick = useCallback(
        (event: React.MouseEvent) => {
            event.stopPropagation();
            const extensions = {
                provisioningPageOptions: {
                    actions: {
                        primary: {
                            behaviour: "land",
                            prompt: "next",
                            options: {
                                targetTab: "same",
                                urlParams: { templateId }
                            }
                        }
                    }
                },
                ...(template.templateId ===
                    "advanced-it-service-management" && {
                    edition: Editions.PREMIUM
                })
            };

            fireUIEvent({
                action: "clicked",
                actionSubject: "button",
                attributes: {
                    label: "Try now",
                    category: templateId
                }
            });

            fireCrossFlowInteractedUIEvent(
                {
                    action: "clicked",
                    actionSubject: "button",
                    actionSubjectId: "templateCard"
                },
                {
                    recommendedProductIds: [JSM_PRODUCT_ID],
                    targetProductId: JSM_PRODUCT_ID,
                    source: "wacJsmTemplateScreen"
                }
            );

            if (openCrossFlow) openCrossFlow(event, {}, extensions);
        },
        [
            fireCrossFlowInteractedUIEvent,
            fireUIEvent,
            openCrossFlow,
            template,
            templateId
        ]
    );

    const onCardHover = useCallback(() => {
        fireUIEvent({
            action: "hover",
            actionSubject: "card",
            attributes: {
                category: messages?.name
            }
        });
    }, [fireUIEvent, messages?.name]);

    if (!template.templateId) return null;

    return (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
            onClick={handleCardClick}
            onMouseEnter={onCardHover}
            css={templateCardContainerStyles}
            data-testid="hover-template-card"
        >
            <div css={imageWrapperStyles}>
                <Picture
                    css={imageStyles}
                    image={{
                        src: template.templateIcon?.refreshUrl ?? "",
                        alt: "" // Intentional left blank as these images are not required for screen readers
                    }}
                />
                <div
                    className="templateHoverStyles"
                    css={templateCardHoverAnchorStyles}
                >
                    <Button appearance="primary" onClick={handleCardClick}>
                        <FormattedMessage
                            id="service-management-use-template-button"
                            defaultMessage="Use template"
                        />
                    </Button>
                    <a
                        href={learnMoreHref}
                        css={templateCardLearnMoreStyles}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <FormattedMessage
                            id="service-management-template-learn-more-link"
                            defaultMessage="Learn more"
                        />
                    </a>
                </div>
            </div>
            <div
                css={templateCardInfoWrapper}
                data-testid="template-card-clickable"
            >
                <h2 css={templateCardTitleStyles}>
                    <FormattedMessage {...messages.name} />
                </h2>
                <p css={templateCardDescriptionStyles}>
                    {intl.formatMessage(messages.oneLinerHeadline)}
                </p>
            </div>
        </div>
    );
};

const fontCharlieText = `"Charlie Text", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Noto Sans", Ubuntu, "Droid Sans", "Helvetica Neue", sans-serif`;
const fontCharlieDisplay = `"Charlie Display", "Charlie Text", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Noto Sans", Ubuntu, "Droid Sans", "Helvetica Neue", sans-serif`;

const templateCardContainerStyles = css({
    "display": "grid",
    "gridTemplateRows": "min-content 1fr",
    "borderRadius": "6px",
    "boxShadow": "0px 1px 2px 0px rgba(9, 30, 66, 0.25)",
    "overflow": "hidden",
    "boxSizing": "border-box",
    "textAlign": "left",
    "cursor": "pointer",
    "border": "none",
    "padding": 0,
    "outline": "none",
    "&:hover, &:focus, &:focus-within": {
        "boxShadow": "0px 1px 4px 0px rgba(9, 30, 66, 0.25)",
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".templateHoverStyles": {
            opacity: 1,
            // eslint-disable-next-line local/ensure-wac-token-usage
            background: "rgb(248, 248, 248, .8)"
        }
    }
});

const imageStyles = css({
    width: "100%",
    height: "100%",
    aspectRatio: "361 / 199"
});

const templateCardInfoWrapper = css({
    padding: "24px",
    display: "grid",
    gap: "8px",
    background: token("elevation.surface")
});

const templateCardTitleStyles = css({
    fontFamily: fontCharlieDisplay,
    fontSize: "20px !important",
    fontWeight: 700,
    lineHeight: "30px !important",
    letterSpacing: "0.3px",
    color: token("color.text"),
    margin: 0
});

const templateCardDescriptionStyles = css({
    fontFamily: fontCharlieText,
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: "24px !important",
    color: token("color.text.subtle"),
    margin: "0px"
});

const templateCardHoverAnchorStyles = css({
    height: "100%",
    width: "100%",
    position: "absolute",
    gap: "20px",
    display: "flex",
    flexDirection: "row",
    insetBlockStart: "0",
    insetInlineStart: "0",
    // eslint-disable-next-line local/ensure-wac-token-usage
    backgroundColor: "#f8f8f8",
    opacity: "0",
    transition: "opacity 0.35s",
    alignItems: "center",
    justifyContent: "center",
    paddingBlockStart: "40px"
});

const templateCardLearnMoreStyles = css({
    "cursor": "pointer",
    "color": token("color.link"),
    "border": "none",
    "justifyContent": "center",
    "fontFamily": fontCharlieText,
    "fontSize": "14px",
    "fontWeight": 600,
    "lineHeight": "20px",
    "gap": "4px",
    "&:hover, &:focus": {
        color: token("color.link.pressed")
    }
});

const imageWrapperStyles = css({
    position: "relative"
});
